import {
    ContentActionView,
    ContentView,
    EnrichedContent,
    ProfileViewWithShadowBan
} from '../../api/client'
import ContentCard from '../content/content-card/ContentCard'
import Spinner from '../Spinner'
import { ThreadNode } from './buildThread'
import GapMarker from './GapMarker'

type Props = {
    // One tree normally. Several when the server could not hydrate the root:
    // the drawer then passes each reply as its own top-level node, so the card
    // markup below is reused rather than duplicated for a flat fallback.
    trees: ThreadNode[]
    // The comment the drawer was opened from. The post page has none.
    focusContentId?: number
    onContentAction: (actions: ContentActionView[], queued?: boolean) => void
    profileCache: ProfileViewWithShadowBan[]
    actionCacheMap: Map<number, ContentActionView[]>
    onMarkAsReadClick: (content: ContentView) => void
    onMarkAsUnreadClick: (content: ContentView) => void

    // The post page's Threaded tab only. The drawer passes none of
    // these, so every reply matches and no button shows, exactly as before.
    //
    // False when only replies matched the filters: the root stays as context, faded.
    rootMatches?: boolean
    // The root is excluded because it has no moderation yet, not by a filter the
    // moderator set; the context tag says so.
    rootAwaitingModeration?: boolean
    // Per reply id; a missing id counts as matching.
    replyMatches?: Map<number, boolean>
    // Excluded replies with no moderation yet. Counted apart in the reply gaps.
    replyAwaitingModeration?: Set<number>
    // Replies held but not yet loaded, and how to load them.
    moreRepliesCount?: number
    onMoreReplies?: () => void
    moreRepliesLoading?: boolean
}

// Shared across renders so ContentCard's props stay referentially stable; the
// drawer never enriches, so the map is always empty.
const EMPTY_ENRICHED = new Map<number, EnrichedContent>()

/**
 * Root first, replies indented one level under a hairline connector. Every
 * node is the real ContentCard with its action row, sharing the feed's action
 * cache so an action here updates the feed card too.
 */
export default function ConversationThread(props: Props) {
    const {
        trees,
        focusContentId,
        rootMatches = true,
        rootAwaitingModeration = false,
        replyMatches,
        replyAwaitingModeration,
        moreRepliesCount,
        onMoreReplies,
        moreRepliesLoading = false,
        ...cardProps
    } = props

    const matches = (node: ThreadNode) =>
        node.depth === 0 ? rootMatches : (replyMatches?.get(node.content.id) ?? true)
    const awaitingModeration = (node: ThreadNode) =>
        node.depth === 0
            ? rootAwaitingModeration
            : (replyAwaitingModeration?.has(node.content.id) ?? false)

    // A reply renders when it or anything under it matches; a non-matching reply
    // with a matching descendant stays as faded context, so no reply ever shows
    // without its parent.
    const renders = (node: ThreadNode): boolean => matches(node) || node.children.some(renders)
    const subtreeSize = (node: ThreadNode): number =>
        1 + node.children.reduce((sum, child) => sum + subtreeSize(child), 0)
    const subtreeAwaiting = (node: ThreadNode): number =>
        (awaitingModeration(node) ? 1 : 0) +
        node.children.reduce((sum, child) => sum + subtreeAwaiting(child), 0)

    // Consecutive children that do not render collapse into one "N replies
    // filtered" marker, counting their own replies too, in the place they fell.
    const renderChildren = (children: ThreadNode[]) => {
        const out: JSX.Element[] = []
        let filtered = 0
        let awaiting = 0
        let firstFilteredId = 0
        const flush = () => {
            if (filtered > 0) {
                out.push(
                    <GapMarker
                        key={`gap-${firstFilteredId}`}
                        count={filtered}
                        awaiting={awaiting}
                        noun="reply"
                    />
                )
            }
            filtered = 0
            awaiting = 0
        }
        for (const child of children) {
            if (renders(child)) {
                flush()
                out.push(renderNode(child))
            } else {
                if (filtered === 0) firstFilteredId = child.content.id
                filtered += subtreeSize(child)
                awaiting += subtreeAwaiting(child)
            }
        }
        flush()
        return out
    }

    const moreButton = onMoreReplies && (
        <div>
            <button
                type="button"
                onClick={onMoreReplies}
                disabled={moreRepliesLoading}
                className="inline-flex items-center gap-2 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-800 shadow-sm hover:bg-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-arwen-500 disabled:opacity-60"
            >
                {moreRepliesLoading && <Spinner size={4} />}
                {moreRepliesCount && moreRepliesCount > 0
                    ? `View ${moreRepliesCount} more ${moreRepliesCount === 1 ? 'reply' : 'replies'}`
                    : 'View more replies'}
            </button>
        </div>
    )

    const renderNode = (node: ThreadNode) => {
        const isFocus = node.content.id === focusContentId
        // Context only: a root or reply the filters exclude, shown because a reply
        // under it matched.
        const faded = !isFocus && !matches(node)
        const children = renderChildren(node.children)
        // The button belongs to the thread, so it hangs off the first root only.
        const more = node.depth === 0 && node === trees[0] ? moreButton : undefined
        const card = (
            <ContentCard
                content={node.content}
                showAccount={false}
                onContentAction={cardProps.onContentAction}
                enriching={false}
                detectedLanguage={null}
                translating={false}
                onTranslateClick={() => {}}
                profileCache={cardProps.profileCache}
                enrichedContentMap={EMPTY_ENRICHED}
                actionCacheMap={cardProps.actionCacheMap}
                rounded={true}
                isSelected={false}
                onSelectionChange={() => {}}
                onMarkAsReadClick={cardProps.onMarkAsReadClick}
                onMarkAsUnreadClick={cardProps.onMarkAsUnreadClick}
            />
        )
        return (
            <div key={node.content.id} className="flex flex-col gap-2">
                {/* The highlight alone marks the opened comment. A text tag clashed with the card
                    rail and named one entry point when the drawer opens from search and pages too. */}
                <div
                    aria-current={isFocus ? 'true' : undefined}
                    className={`relative rounded-lg border ${
                        isFocus ? 'border-ink-300 bg-ink-50' : 'border-ink-200 bg-white'
                    }`}
                >
                    {faded && (
                        // Outside the faded wrapper so the tag itself stays legible.
                        <div className="px-3 pt-1 text-[10px] font-semibold uppercase tracking-widest text-ink-500">
                            {awaitingModeration(node)
                                ? 'Awaiting moderation'
                                : "Doesn't match filters"}
                        </div>
                    )}
                    {faded ? <div className="opacity-70">{card}</div> : card}
                </div>
                {(children.length > 0 || more) &&
                    (node.depth === 0 ? (
                        // One indented level only: Meta, YouTube and TikTok nest a single level, and
                        // Twitter-style chains would squeeze a 560px drawer if every level indented.
                        // The connector is a hairline, not a coloured card border: ink-300, matching the mock.
                        <div className="relative ml-7 flex flex-col gap-2 border-l-2 border-ink-300 pl-4">
                            {children}
                            {more}
                        </div>
                    ) : (
                        // Deeper replies sit at the depth-1 indent, in tree order.
                        <div className="flex flex-col gap-2">{children}</div>
                    ))}
            </div>
        )
    }

    return <div className="mt-3 flex flex-col gap-2">{trees.map(renderNode)}</div>
}
